/**
 * MailboxComposeDialog — compose + send over a connected account's SMTP
 * (external-client rebuild). Distinct from the legacy ComposeDialog (internal
 * @fias.email mail), which Phase 6 re-layers/removes.
 *
 * From-account select, To/Cc/Bcc (comma/semicolon-separated), Subject, a
 * rich-text (HTML) body via ComposeEditor, and attachments (Vault documents —
 * picked or uploaded via the shared VaultFilePickerDialog, the same path
 * Travel/Social/Image use). Sends via useSendEmail (multipart text+html); the
 * Sent copy appears on the next list refresh.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Paperclip, X, Clock, Sparkles, Upload, FolderOpen, Trash2 } from 'lucide-react';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';
import { ComposeEditor, type ComposeEditorHandle } from './ComposeEditor';
import { RecipientInput } from './RecipientInput';
import { uploadLocalFileToVault } from '@/lib/vault-upload';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useSendEmail, useScheduleSend, useSaveDraft } from '../hooks/useMailbox';
import {
  useEmailTemplates,
  useEmailSnippets,
  useEmailSignatures,
} from '../hooks/useComposerAssets';
import { useDraftReply } from '../hooks/useEmailAI';
import { useEmailUserSettings } from '../hooks/useEmailUserSettings';
import type { EmailConnection } from '@/lib/api';

/** Convert plain text (reply quotes, forwards, legacy/text-only drafts) to
 *  simple HTML for the rich editor: blank lines split paragraphs, single
 *  newlines become <br>, and the text is HTML-escaped so a literal "<" in the
 *  quoted original can't inject markup. */
function textToHtml(text: string): string {
  if (!text) return '';
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${escape(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Rewrite the editor's inline images for the wire: each `<img data-doc-id="…">`
 * (a Vault-uploaded image shown in the editor via a local data-URI preview) is
 * turned into `<img src="cid:<documentId>">` and the ids are collected so the
 * server attaches the Vault bytes as inline CID parts. This keeps the sent HTML
 * (and the persisted Sent copy) small and lets the reader resolve the images via
 * the same `cid:` path as received mail. Exported for unit testing.
 */
export function extractInlineImages(html: string): { html: string; documentIds: string[] } {
  if (!html.includes('data-doc-id') || typeof DOMParser === 'undefined') {
    return { html, documentIds: [] };
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const ids: string[] = [];
  doc.querySelectorAll('img[data-doc-id]').forEach((img) => {
    const id = img.getAttribute('data-doc-id');
    if (!id) return;
    ids.push(id);
    img.setAttribute('src', `cid:${id}`);
    img.removeAttribute('data-doc-id');
  });
  return { html: doc.body.innerHTML, documentIds: [...new Set(ids)] };
}

export interface ComposeInitialDraft {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  /** Plain-text seed (reply quote / forward). Converted to HTML for the editor
   *  when no `bodyHtml` is supplied. */
  body?: string;
  /** HTML seed — preferred when present (resuming an HTML draft). */
  bodyHtml?: string;
  inReplyToMessageId?: string;
  /** When resuming a Drafts-folder message: the draft id to supersede on
   *  save/send so re-saving doesn't pile up duplicate drafts. */
  draftMessageId?: string;
  /** Pre-attached Vault documents (resuming a draft that had attachments). */
  attachments?: VaultPickerResult[];
}

interface MailboxComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: EmailConnection[];
  defaultAccountId?: string;
  initialDraft?: ComposeInitialDraft;
  /** When resuming an existing draft: discard it (move to Trash) and close.
   *  Only wired when `initialDraft.draftMessageId` is set. */
  onDiscardDraft?: () => void;
  /** Re-open the composer seeded with `draft` — used by undo-send to bring the
   *  message back after the user cancels the in-flight dispatch. */
  onRestoreDraft?: (draft: ComposeInitialDraft) => void;
}

function parseRecipients(raw: string): { email: string }[] {
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((email) => ({ email }));
}

export function MailboxComposeDialog({
  open,
  onOpenChange,
  accounts,
  defaultAccountId,
  initialDraft,
  onDiscardDraft,
  onRestoreDraft,
}: MailboxComposeDialogProps) {
  const send = useSendEmail();
  const scheduleSend = useScheduleSend();
  const saveDraft = useSaveDraft();
  const [accountId, setAccountId] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState('');
  // The rich editor owns the body; these mirror its current HTML + plain-text
  // for the send payload and validation. `editorRef` drives template / snippet /
  // signature / AI-draft insertion.
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const editorRef = useRef<ComposeEditorHandle>(null);
  const handleEditorChange = useCallback((html: string, text: string) => {
    setBodyHtml(html);
    setBodyText(text);
  }, []);
  // Computed fresh at each mount of the editor (the dialog unmounts its content
  // when closed, so the editor re-seeds via this on every open).
  const initialBodyHtml = initialDraft?.bodyHtml ?? textToHtml(initialDraft?.body ?? '');
  const [attachments, setAttachments] = useState<VaultPickerResult[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Inline-image picker ("Insert image from Fias"): a separate single-select,
  // image-filtered picker. `pickVaultImage` opens it and returns a promise the
  // picker's callbacks settle (pick → the doc; cancel/close → null).
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const imagePickResolveRef = useRef<
    ((r: { documentId: string; url: string } | null) => void) | null
  >(null);
  // The editor initializes asynchronously; its imperative handle no-ops until
  // ready. Track readiness so the auto-signature append doesn't race init.
  const [editorReady, setEditorReady] = useState(false);
  // Local-file upload (choose / drag-drop). Attachments must be Vault docs, so a
  // local file is uploaded to the user's Personal Holder first, then attached.
  const [uploadHolderId, setUploadHolderId] = useState<string | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Drag enter/leave bubble from child elements, so a naive boolean flickers
  // across a large drop target. Track nesting depth and only clear at zero.
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Keys (name:size) of local files whose upload is in flight. Checked
  // synchronously so a fast double-drop can't start the same upload twice
  // before the first one finalizes into `attachments`.
  const pendingLocalKeys = useRef<Set<string>>(new Set());
  const [scheduleAt, setScheduleAt] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);

  const { data: templates } = useEmailTemplates(accountId || undefined);
  const { data: snippets } = useEmailSnippets(accountId || undefined);
  const { data: signatures } = useEmailSignatures(accountId || undefined);
  const draft = useDraftReply();
  const [aiInstructions, setAiInstructions] = useState('');

  // Holder-wide compose prefs (global across accounts). Scheduled send gates the
  // Schedule control; undo-send holds dispatch behind a Cancel window; auto-sig
  // inserts the default signature on a fresh compose.
  const userSettings = useEmailUserSettings();
  const scheduleEnabled = userSettings.features.scheduleEnabled;
  const undoSend = userSettings.undoSend;
  const autoSignature = userSettings.autoSignature;
  useEffect(() => {
    if (!scheduleEnabled) setShowSchedule(false);
  }, [scheduleEnabled]);

  // Undo-send window (Apple-style): on Send the composer closes immediately and
  // an "Undo" toast holds the dispatch for N seconds. Undo cancels the send and
  // re-opens the composer. The timeout lives here (the dialog stays mounted; only
  // its content unmounts on close), so the pending send survives the close.
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // (Re)seed the form whenever the dialog opens, so each compose/reply starts fresh.
  useEffect(() => {
    if (!open) return;
    setAccountId(
      defaultAccountId ??
        accounts.find((a) => a.isPrimary)?.accountId ??
        accounts[0]?.accountId ??
        '',
    );
    setTo(initialDraft?.to ?? '');
    setCc(initialDraft?.cc ?? '');
    setBcc(initialDraft?.bcc ?? '');
    setShowCcBcc(Boolean(initialDraft?.cc || initialDraft?.bcc));
    setSubject(initialDraft?.subject ?? '');
    // Body is owned by ComposeEditor — it re-seeds itself from `initialBodyHtml`
    // on (re)mount and reports back through `handleEditorChange`, so we must NOT
    // reset bodyHtml/bodyText here (that would clobber the editor's seed).
    setAttachments(initialDraft?.attachments ?? []);
    setUploadingCount(0);
    pendingLocalKeys.current = new Set();
    dragDepth.current = 0;
    setIsDragging(false);
    setScheduleAt('');
    setShowSchedule(false);
    setAiInstructions('');
    send.reset();
    scheduleSend.reset();
    saveDraft.reset();
    draft.reset();
    // Abort any in-flight draft stream when the dialog closes/reopens, so a
    // late token can't write into a stale composer.
    return () => draft.cancel();
    // Only re-seed on open transitions.
  }, [open]);

  const recipients = parseRecipients(to);
  const pending = send.isPending || scheduleSend.isPending;
  const scheduleDate = scheduleAt ? new Date(scheduleAt) : null;
  const scheduleValid =
    !!scheduleDate && !Number.isNaN(scheduleDate.getTime()) && scheduleDate.getTime() > Date.now();
  const willSchedule = showSchedule && scheduleValid;
  // Block send while local attachments are still uploading — otherwise
  // buildPayload() captures the attachments array mid-upload and silently
  // omits the in-flight files. The "Uploading N…" indicator shows why.
  const canSubmit =
    !!accountId &&
    recipients.length > 0 &&
    !pending &&
    uploadingCount === 0 &&
    (!showSchedule || scheduleValid);

  // A draft just needs an account + something worth keeping (and no in-flight
  // upload). Recipients are NOT required — that's the point of a draft.
  const hasContent =
    recipients.length > 0 ||
    !!subject.trim() ||
    !!bodyText.trim() ||
    // An image-only body contributes no plain text — count inline images too.
    bodyHtml.includes('data-doc-id') ||
    attachments.length > 0 ||
    !!cc.trim() ||
    !!bcc.trim();
  const canSaveDraft =
    !!accountId && hasContent && !pending && !saveDraft.isPending && uploadingCount === 0;

  function buildPayload() {
    // Inline images ride as CID parts: rewrite each editor `<img data-doc-id>` to
    // `src="cid:<documentId>"` (dropping the local data-URI preview) and collect
    // the Vault document ids so the server attaches the bytes as inline parts.
    // Send an HTML part when there's formatted text OR an inline image. Gating
    // on bodyText alone drops an image-only message: TipTap image nodes carry no
    // text, so bodyText is empty and both html and inlineImageDocumentIds would
    // be omitted — the send appears to succeed but arrives blank.
    const hasHtmlBody = !!bodyText.trim() || bodyHtml.includes('data-doc-id');
    const { html: sendHtml, documentIds: inlineImageDocumentIds } = hasHtmlBody
      ? extractInlineImages(bodyHtml)
      : { html: undefined as string | undefined, documentIds: [] as string[] };
    return {
      accountId,
      to: recipients,
      cc: showCcBcc && cc.trim() ? parseRecipients(cc) : undefined,
      bcc: showCcBcc && bcc.trim() ? parseRecipients(bcc) : undefined,
      subject,
      // Send multipart text+html: bodyText is the editor's plain-text rendering
      // (fallback for text-only clients), bodyHtml the formatted version.
      text: bodyText,
      html: sendHtml,
      inReplyToMessageId: initialDraft?.inReplyToMessageId,
      attachmentDocumentIds: attachments.length ? attachments.map((a) => a.documentId) : undefined,
      inlineImageDocumentIds: inlineImageDocumentIds.length ? inlineImageDocumentIds : undefined,
      // Sending/scheduling a resumed draft removes the source draft on success.
      replacesDraftId: initialDraft?.draftMessageId,
    };
  }

  // A ComposeInitialDraft snapshot of the current form, so undo can re-open the
  // composer exactly as it was (bodyHtml keeps formatting + inline images).
  const currentDraftSnapshot = (): ComposeInitialDraft => ({
    to,
    cc,
    bcc,
    subject,
    bodyHtml,
    inReplyToMessageId: initialDraft?.inReplyToMessageId,
    draftMessageId: initialDraft?.draftMessageId,
    attachments,
  });

  // Apple-style undo-send: close the composer now, hold the dispatch behind an
  // "Undo" toast for N seconds, then send. Undo cancels the timeout and re-opens
  // the composer with the message intact.
  const startUndoableSend = () => {
    const payload = buildPayload();
    const restore = currentDraftSnapshot();
    onOpenChange(false);
    undoTimeoutRef.current = setTimeout(() => {
      undoTimeoutRef.current = null;
      send.mutate(payload, {
        onError: (e) =>
          toast({
            title: 'Send failed',
            description: e instanceof Error ? e.message : String(e),
            variant: 'destructive',
          }),
      });
    }, undoSend.seconds * 1000);
    toast({
      description: 'Sending your message…',
      duration: undoSend.seconds * 1000,
      action: (
        <ToastAction
          altText="Undo send"
          onClick={() => {
            if (undoTimeoutRef.current) {
              clearTimeout(undoTimeoutRef.current);
              undoTimeoutRef.current = null;
            }
            onRestoreDraft?.(restore);
          }}
        >
          Undo
        </ToastAction>
      ),
    });
  };

  // Clear a pending dispatch only on full unmount (the dialog stays mounted
  // across open/close, so the send survives the composer closing — that's the
  // point of the Apple-style window).
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    };
  }, []);

  // The editor unmounts on close and re-mounts (re-firing onReady) on the next
  // open, so drop readiness when closed to force a fresh wait each open.
  useEffect(() => {
    if (!open) setEditorReady(false);
  }, [open]);

  // On opening a reply / reply-all, put the cursor at the TOP of the body (above
  // the quoted message) and focus the editor so the user can type immediately.
  const replyFocusedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      replyFocusedRef.current = false;
      return;
    }
    if (replyFocusedRef.current || !editorReady || !initialDraft?.inReplyToMessageId) return;
    replyFocusedRef.current = true;
    editorRef.current?.focus('start');
  }, [open, editorReady, initialDraft?.inReplyToMessageId]);

  // Auto-signature, kept in sync with the From account. On a FRESH compose the
  // body is blank, so the only "-- " delimiter is ours: we can safely SWAP the
  // trailing signature for the new account's default (or drop it if that account
  // has none) when the From account changes — the report was that switching From
  // left the previous account's signature behind. On a reply/forward the quoted
  // original may carry its own "-- " delimiter, so we never swap there (that
  // could delete the quote) — we just append the default once. Never runs when
  // resuming an existing draft (it already has its body). `sigKey` tracks the
  // account + resolved default so a swap re-fires once the new account's
  // (account-keyed) signatures finish loading, not on the transient empty.
  const isFreshCompose = !initialDraft?.draftMessageId && initialBodyHtml.trim() === '';
  const sigKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      sigKeyRef.current = null;
      return;
    }
    if (!autoSignature || initialDraft?.draftMessageId || !editorReady || !accountId) return;
    if (signatures === undefined) return; // wait for THIS account's signatures
    const def = signatures.find((s) => s.isDefault);
    const key = `${accountId}:${def?.signatureId ?? 'none'}`;
    if (sigKeyRef.current === key) return;
    const firstApply = sigKeyRef.current === null;
    sigKeyRef.current = key;
    if (isFreshCompose) {
      editorRef.current?.replaceSignature(def?.bodyHtml ?? null);
    } else if (firstApply && def) {
      editorRef.current?.appendHtml(`<p>-- </p>${def.bodyHtml}`);
    }
  }, [
    open,
    accountId,
    autoSignature,
    signatures,
    initialDraft?.draftMessageId,
    editorReady,
    isFreshCompose,
  ]);

  const handleSaveDraft = () => {
    saveDraft.mutate(buildPayload(), { onSuccess: () => onOpenChange(false) });
  };

  const handleSubmit = () => {
    if (willSchedule) {
      scheduleSend.mutate(
        { ...buildPayload(), sendAt: scheduleDate!.toISOString() },
        { onSuccess: () => onOpenChange(false) },
      );
      return;
    }
    // Undo-send: close now, hold the dispatch behind an "Undo" toast.
    if (undoSend.enabled) {
      startUndoableSend();
      return;
    }
    send.mutate(buildPayload(), { onSuccess: () => onOpenChange(false) });
  };

  // Kick off an AI draft (shared by the Draft button and the Enter key in the
  // instructions field). Pins the existing body HTML once, then streams the
  // generated text (as HTML) in after it as tokens arrive; onDone applies the
  // final (preamble-stripped) text.
  const runAiDraft = () => {
    if (!aiInstructions.trim() || draft.isPending) return;
    const prefixHtml = bodyText.trim() ? bodyHtml : '';
    let acc = '';
    draft.start(
      { instructions: aiInstructions.trim(), replyToMessageId: initialDraft?.inReplyToMessageId },
      {
        onToken: (chunk) => {
          acc += chunk;
          editorRef.current?.setHtml(prefixHtml + textToHtml(acc));
        },
        onDone: (output) => editorRef.current?.setHtml(prefixHtml + textToHtml(output)),
      },
    );
  };

  const error = (send.error ?? scheduleSend.error) as Error | null;

  // Composer assets store HTML — insert it directly so formatting is preserved
  // (the editor schema drops anything unsafe on parse). Template replaces the
  // body; snippet inserts at the cursor; signature appends after a "-- " rule.
  const applyTemplate = (templateId: string) => {
    const t = templates?.find((x) => x.templateId === templateId);
    if (!t) return;
    if (t.subject) setSubject(t.subject);
    editorRef.current?.setHtml(t.bodyHtml ?? '');
  };
  const insertSnippet = (snippetId: string) => {
    const s = snippets?.find((x) => x.snippetId === snippetId);
    if (!s) return;
    editorRef.current?.insertHtml(s.body);
  };
  const insertSignature = (signatureId: string) => {
    const s = signatures?.find((x) => x.signatureId === signatureId);
    if (!s) return;
    editorRef.current?.appendHtml(`<p>-- </p>${s.bodyHtml}`);
  };

  const addAttachments = (files: VaultPickerResult[]) => {
    setAttachments((prev) => {
      const seen = new Set(prev.map((a) => a.documentId));
      return [...prev, ...files.filter((f) => !seen.has(f.documentId))];
    });
  };

  // Upload an inline image to the Vault (Personal Holder) and hand its document
  // id back to the editor. Unlike attachments, inline images are NOT added to the
  // attachment tray — they live in the body and are attached as CID parts at send.
  const uploadInlineImage = useCallback(
    async (file: File): Promise<string> => {
      if (!uploadHolderId) throw new Error('No upload destination');
      const up = await uploadLocalFileToVault(file, uploadHolderId, {
        purpose: 'outgoing-email-attachment',
      });
      return up.documentId;
    },
    [uploadHolderId],
  );

  // Settle the pending "Insert image from Fias" promise exactly once (nulls the
  // ref first, so a pick's onFileSelected and the subsequent onOpenChange can't
  // double-resolve) and close the picker.
  const settleImagePick = useCallback((r: { documentId: string; url: string } | null) => {
    const resolve = imagePickResolveRef.current;
    imagePickResolveRef.current = null;
    setImagePickerOpen(false);
    resolve?.(r);
  }, []);

  const pickVaultImage = useCallback(
    () =>
      new Promise<{ documentId: string; url: string } | null>((resolve) => {
        imagePickResolveRef.current = resolve;
        setImagePickerOpen(true);
      }),
    [],
  );

  // Resolve the Personal Holder once the dialog opens, so local uploads have a
  // destination (mirrors VaultFilePickerDialog).
  useEffect(() => {
    if (!open || uploadHolderId) return;
    let cancelled = false;
    void api
      .listMyVaultMemberships()
      .then((m) => {
        if (!cancelled && m.personalHolder) setUploadHolderId(m.personalHolder.holderId);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, uploadHolderId]);

  // Upload local files to the Vault, then attach. Only added AFTER finalize
  // resolves (a failed upload leaves no attachment row). Skips a file whose
  // name+size already matches an attachment, so an accidental double-drop /
  // re-choose doesn't add duplicate rows.
  const addLocalFiles = async (files: File[]) => {
    if (files.length === 0) return;
    if (!uploadHolderId) {
      toast({
        title: 'Workspace not ready',
        description: 'Your personal workspace is still loading — try again in a moment.',
        variant: 'destructive',
      });
      return;
    }
    // Skip a file already attached OR already uploading (the latter guards a
    // fast double-drop before the first upload finalizes). Reserve the key
    // synchronously so the second call sees it.
    const completed = new Set(attachments.map((a) => `${a.fileName}:${a.sizeBytes}`));
    const fresh: File[] = [];
    for (const f of files) {
      const key = `${f.name}:${f.size}`;
      if (completed.has(key) || pendingLocalKeys.current.has(key)) continue;
      pendingLocalKeys.current.add(key);
      fresh.push(f);
    }
    if (fresh.length === 0) return;
    setUploadingCount((n) => n + fresh.length);
    for (const file of fresh) {
      const key = `${file.name}:${file.size}`;
      try {
        const up = await uploadLocalFileToVault(file, uploadHolderId, {
          purpose: 'outgoing-email-attachment',
        });
        setAttachments((prev) =>
          prev.some((a) => a.documentId === up.documentId)
            ? prev
            : [
                ...prev,
                {
                  documentId: up.documentId,
                  fileName: up.fileName,
                  mimeType: up.mimeType,
                  sizeBytes: up.sizeBytes,
                  s3Key: up.s3Key,
                  downloadUrl: '',
                },
              ],
        );
      } catch (err) {
        toast({
          title: 'Upload failed',
          description: `${file.name}: ${err instanceof Error ? err.message : String(err)}`,
          variant: 'destructive',
        });
      } finally {
        pendingLocalKeys.current.delete(key);
        setUploadingCount((n) => n - 1);
      }
    }
  };

  // Drag-and-drop covers the WHOLE dialog (not just the attachment box), with a
  // clear overlay. Only react to actual file drags so a text/selection drag
  // doesn't pop the overlay. The overlay is pointer-events-none so the drop
  // still lands on the dialog beneath it.
  const isFileDrag = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const dropZoneHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) {
        dragDepth.current = 0;
        setIsDragging(false);
      }
    },
    onDrop: (e: React.DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      void addLocalFiles(Array.from(e.dataTransfer.files));
    },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A half-written message is easy to lose, so the composer can only be
          dismissed via its explicit actions (Cancel / Send / Schedule) — not
          by clicking the backdrop, pressing Escape, or a stray top-right X. */}
      <DialogContent
        hideClose
        // NB: no `relative` here — it would override the base `.fixed` (Tailwind
        // orders position utilities fixed→…→relative, so relative wins) and drop
        // the centered modal below the viewport. `position: fixed` already
        // establishes the containing block the drop overlay's `absolute inset-0`
        // positions against.
        className="sm:max-w-4xl"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        {...dropZoneHandlers}
      >
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-background/90 text-primary">
            <Upload className="h-8 w-8" />
            <p className="text-sm font-medium">Drop files to attach</p>
          </div>
        )}
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
        </DialogHeader>
        {/* px padding gives focus rings room on the left (overflow-y-auto also
            clips overflow-x, so a flush field's focus ring was cut off) and
            keeps content clear of the scrollbar on the right. */}
        <div className="max-h-[65vh] space-y-3 overflow-y-auto pl-2 pr-3">
          <div className="space-y-1">
            <Label htmlFor="compose-from">From</Label>
            <select
              id="compose-from"
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.emailAddress}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="compose-to">To</Label>
              {!showCcBcc && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={() => setShowCcBcc(true)}
                >
                  Cc/Bcc
                </button>
              )}
            </div>
            <RecipientInput
              id="compose-to"
              value={to}
              onChange={setTo}
              placeholder="name@example.com, another@example.com"
              accountId={accountId || undefined}
            />
          </div>
          {showCcBcc && (
            <>
              <div className="space-y-1">
                <Label htmlFor="compose-cc">Cc</Label>
                <RecipientInput
                  id="compose-cc"
                  value={cc}
                  onChange={setCc}
                  accountId={accountId || undefined}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="compose-bcc">Bcc</Label>
                <RecipientInput
                  id="compose-bcc"
                  value={bcc}
                  onChange={setBcc}
                  accountId={accountId || undefined}
                />
              </div>
            </>
          )}
          <div className="space-y-1">
            <Label htmlFor="compose-subject">Subject</Label>
            <Input
              id="compose-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="compose-body">Message</Label>
            <ComposeEditor
              ref={editorRef}
              initialHtml={initialBodyHtml}
              onChange={handleEditorChange}
              onUploadImage={uploadHolderId ? uploadInlineImage : undefined}
              onPickVaultImage={uploadHolderId ? pickVaultImage : undefined}
              onReady={() => setEditorReady(true)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Input
              aria-label="AI draft instructions"
              placeholder="Tell AI what to write…"
              value={aiInstructions}
              onChange={(e) => setAiInstructions(e.target.value)}
              onKeyDown={(e) => {
                // Enter drafts (matching the button); Shift+Enter is unused here
                // (single-line input) but left alone. Skip while an IME is composing.
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  runAiDraft();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 shrink-0"
              disabled={!aiInstructions.trim() || draft.isPending}
              onClick={runAiDraft}
            >
              {draft.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Draft
            </Button>
          </div>
          {draft.isError && (
            <p className="text-xs text-destructive">{(draft.error as Error)?.message}</p>
          )}

          {(!!templates?.length || !!snippets?.length || !!signatures?.length) && (
            <div className="flex flex-wrap gap-2">
              {!!templates?.length && (
                <select
                  aria-label="Insert template"
                  className="h-8 rounded-md border bg-background px-2 text-xs"
                  value=""
                  onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                >
                  <option value="">Template…</option>
                  {templates.map((t) => (
                    <option key={t.templateId} value={t.templateId}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
              {!!snippets?.length && (
                <select
                  aria-label="Insert snippet"
                  className="h-8 rounded-md border bg-background px-2 text-xs"
                  value=""
                  onChange={(e) => e.target.value && insertSnippet(e.target.value)}
                >
                  <option value="">Snippet…</option>
                  {snippets.map((s) => (
                    <option key={s.snippetId} value={s.snippetId}>
                      {s.trigger}
                    </option>
                  ))}
                </select>
              )}
              {!!signatures?.length && (
                <select
                  aria-label="Insert signature"
                  className="h-8 rounded-md border bg-background px-2 text-xs"
                  value=""
                  onChange={(e) => e.target.value && insertSignature(e.target.value)}
                >
                  <option value="">Signature…</option>
                  {signatures.map((s) => (
                    <option key={s.signatureId} value={s.signatureId}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Attachments. Dropping files anywhere on the dialog attaches them
              (see the dialog-level drop overlay); this box holds the list of
              attached files. The attach buttons live in the pinned action bar
              below so they're always reachable. */}
          <div className="space-y-2 rounded-md border border-dashed border-border p-2">
            {attachments.length > 0 && (
              <ul className="space-y-1">
                {attachments.map((a) => (
                  <li
                    key={a.documentId}
                    className="flex items-center justify-between gap-2 rounded border bg-card px-2 py-1 text-sm"
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{a.fileName}</span>
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${a.fileName}`}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setAttachments((prev) => prev.filter((x) => x.documentId !== a.documentId))
                      }
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                void addLocalFiles(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Or drag files anywhere onto this window to attach.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error.message}</p>}
        </div>
        {/* "Send at" is pinned above the action bar (not inside the scrollable
            body) so toggling Schedule reveals the field in view rather than
            below the fold. */}
        {scheduleEnabled && showSchedule && (
          <div className="space-y-1 border-t pt-3">
            <Label htmlFor="compose-schedule">Send at</Label>
            <Input
              id="compose-schedule"
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
            {scheduleAt && !scheduleValid && (
              <p className="text-xs text-destructive">Pick a time in the future.</p>
            )}
          </div>
        )}
        {/* Attach + Schedule live in this always-visible action bar, not the
            scrollable body — otherwise a tall message body pushes them below
            the fold and users miss them entirely. */}
        <DialogFooter className={showSchedule ? '' : 'border-t pt-3'}>
          <div
            data-testid="compose-action-bar"
            className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex flex-wrap items-center gap-2">
              {initialDraft?.draftMessageId && onDiscardDraft && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-destructive hover:text-destructive"
                  onClick={onDiscardDraft}
                >
                  <Trash2 className="h-4 w-4" /> Discard draft
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" /> Attach from device
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => setPickerOpen(true)}
              >
                <FolderOpen className="h-4 w-4" /> Attach from Vault
              </Button>
              {scheduleEnabled && (
                <Button
                  type="button"
                  variant={showSchedule ? 'secondary' : 'outline'}
                  size="sm"
                  className="gap-1"
                  aria-pressed={showSchedule}
                  onClick={() => setShowSchedule((v) => !v)}
                >
                  <Clock className="h-4 w-4" /> Schedule
                </Button>
              )}
              {uploadingCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading {uploadingCount}…
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending || saveDraft.isPending}
              >
                Cancel
              </Button>
              <Button variant="outline" onClick={handleSaveDraft} disabled={!canSaveDraft}>
                {saveDraft.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Save as draft'
                )}
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {pending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : willSchedule ? (
                  'Schedule send'
                ) : (
                  'Send'
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      <VaultFilePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        multiSelect
        title="Attach files"
        confirmLabel="Attach"
        onFilesSelected={(files) => addAttachments(files)}
      />

      <VaultFilePickerDialog
        open={imagePickerOpen}
        onOpenChange={(o) => {
          // Closing without a pick (cancel / backdrop) resolves the promise null.
          if (!o) settleImagePick(null);
        }}
        mimeFilter="image"
        title="Insert image from Fias"
        confirmLabel="Insert"
        onFileSelected={(file) =>
          settleImagePick({ documentId: file.documentId, url: file.downloadUrl ?? '' })
        }
      />
    </Dialog>
  );
}
